import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Progress, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { loadSurveys, getResultsForSurvey, loadResponses } from '../api/surveys';

const { Title, Text } = Typography;

export default function SurveyResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const survey = loadSurveys().find(s => s.id === id);

  if (!survey) return <div style={{ padding: 24 }}><Text type="danger">Survey not found.</Text></div>;

  const results = getResultsForSurvey(survey);
  const localResponses = loadResponses().filter(r => r.surveyId === survey.id).length;
  const totalResponses = survey.responseCount + (survey.id.startsWith('seed-') ? 0 : 0);
  const displayCount = survey.id.startsWith('seed-') ? survey.responseCount + localResponses : survey.responseCount;

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/')} style={{ marginBottom: 16 }}>
        Back to Surveys
      </Button>

      <Title level={3} style={{ marginBottom: 4 }}>{survey.title}</Title>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <Tag>{survey.category}</Tag>
        <Text type="secondary">{displayCount} responses · {survey.city}</Text>
        <Tag color={survey.closedAt ? 'default' : 'green'}>{survey.closedAt ? 'Closed' : 'Open'}</Tag>
      </div>

      {survey.questions.map(q => {
        const qResults = results[q.id] ?? {};
        const total = Object.values(qResults).reduce((a, b) => a + b, 0);

        return (
          <Card key={q.id} style={{ marginBottom: 16 }} title={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Tag color="blue">{q.type}</Tag>
              <span style={{ fontWeight: 600 }}>{q.text}</span>
            </div>
          }>
            {q.type === 'text' ? (
              <Text type="secondary">Open-ended responses are not shown publicly.</Text>
            ) : q.type === 'rating' ? (
              <div>
                {Object.entries(qResults)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([rating, count]) => (
                    <div key={rating} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <Text style={{ width: 60 }}>{'★'.repeat(Number(rating))} {rating}/{q.maxRating ?? 5}</Text>
                      <Progress
                        percent={total > 0 ? Math.round((count / total) * 100) : 0}
                        format={() => `${count}`}
                        style={{ flex: 1 }}
                        strokeColor="#faad14"
                      />
                    </div>
                  ))}
                {total === 0 && <Text type="secondary">No responses yet.</Text>}
                {total > 0 && (() => {
                  const avg = Object.entries(qResults).reduce((s, [r, c]) => s + Number(r) * c, 0) / total;
                  return <Text type="secondary">Average: {avg.toFixed(1)} / {q.maxRating ?? 5}</Text>;
                })()}
              </div>
            ) : (
              <div>
                {Object.entries(qResults)
                  .sort((a, b) => b[1] - a[1])
                  .map(([option, count]) => (
                    <div key={option} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <Text style={{ width: 200, flexShrink: 0, fontSize: 13 }}>{option}</Text>
                      <Progress
                        percent={total > 0 ? Math.round((count / total) * 100) : 0}
                        format={() => `${count} (${total > 0 ? Math.round((count / total) * 100) : 0}%)`}
                        style={{ flex: 1 }}
                      />
                    </div>
                  ))}
                {total === 0 && <Text type="secondary">No responses yet.</Text>}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
